import {
  DeliveryTargetStatus,
  FilterReason
} from 'core/rtbCampaign/RtbCampaign';

export type StatusTone = 'warning' | 'danger';

export type DeliveryTargetUnitKey = 'store' | 'device';

/** 與統計卡的三個異常格一一對應。 */
export type AbnormalCategory = 'weather' | 'change' | 'overBindingLimit';

/** 呈現順序固定，與統計卡由左到右一致。 */
export const ABNORMAL_CATEGORY_ORDER: AbnormalCategory[] = [
  'weather',
  'change',
  'overBindingLimit'
];

export type StatusIndex = Map<string, DeliveryTargetStatus>;

/**
 * 依門市 value 索引狀態，`undefined` 代表後端根本沒回這個欄位（整個功能退回現況）。
 *
 * 判 `== null` 而不是 `_.isEmpty` 或 `?.length`：空陣列是「回了，沒有異常門市」，
 * null 是「沒回」，兩者要走不同的畫面。那兩個寫法會把它們混成同一件事。
 *
 * 同一個 value 在門市清單裡可以出現多次（刻意不去重），共用同一筆狀態。
 */
export function buildStatusIndex(
  statuses: DeliveryTargetStatus[] | null | undefined
): StatusIndex | undefined {
  if (statuses == null) {
    return undefined;
  }
  return new Map(
    statuses.map((status) => [status.deliveryTargetValue, status])
  );
}

/**
 * 統計卡的三個異常分類。縣市列的 pill 與門市標籤的顏色都由它導出，
 * 三者才會是同一個口徑 —— 縣市 pill 相加等於該縣市的異常門市數。
 */
export const ABNORMAL_CATEGORIES: {
  [category in AbnormalCategory]: {
    tone: StatusTone;
    i18nKey: (unitKey: DeliveryTargetUnitKey) => string;
  };
} = {
  weather: {
    tone: 'warning',
    i18nKey: () => 'campaignList.labels.weatherMismatch'
  },
  change: {
    tone: 'danger',
    i18nKey: (unitKey) => `campaignList.labels.${unitKey}Change`
  },
  overBindingLimit: {
    tone: 'danger',
    i18nKey: () => 'limitation.hints.overBindingLimit'
  }
};

// 同播不符與已刪除都併進門市異動，與活動列表的 storeChangeCount 加法一致。
const CATEGORY_OF_REASON: { [reason in FilterReason]: AbnormalCategory } = {
  [FilterReason.SPACE_DELETED]: 'change',
  [FilterReason.WEATHER]: 'weather',
  [FilterReason.DELIVERY_TARGET_CHANGE]: 'change',
  [FilterReason.SIMULCAST_MISMATCH]: 'change',
  [FilterReason.STORE_OVER_BINDING_LIMIT]: 'overBindingLimit'
};

/**
 * 只讀 `reasons[0]`。後端已經依它的去重優先序排好，而 `reasons[0]` 正是這間門市
 * 被算進哪一個統計卡數字 —— 在這裡改成掃整個陣列找「比較嚴重」的原因，等於前端
 * 自己實作一次優先序，兩邊一旦不同步，標籤顏色與統計卡就會對不起來。
 */
export function statusCategory(
  status: DeliveryTargetStatus | undefined
): AbnormalCategory | undefined {
  const primary = status?.reasons?.[0];
  return primary === undefined ? undefined : CATEGORY_OF_REASON[primary];
}

export function statusTone(
  status: DeliveryTargetStatus | undefined
): StatusTone | undefined {
  const category = statusCategory(status);
  return category && ABNORMAL_CATEGORIES[category].tone;
}

const REASON_I18N_KEYS: {
  [reason in FilterReason]: (unitKey: DeliveryTargetUnitKey) => string;
} = {
  // 已刪除與門市異動同色，只能靠這裡講出差別。
  [FilterReason.SPACE_DELETED]: () => 'spaceHealth.labels.deleted',
  [FilterReason.DELIVERY_TARGET_CHANGE]: (unitKey) =>
    `campaignList.labels.${unitKey}Change`,
  [FilterReason.WEATHER]: () => 'campaignList.labels.weatherMismatch',
  [FilterReason.SIMULCAST_MISMATCH]: () =>
    'limitation.labels.simulcastMismatch',
  [FilterReason.STORE_OVER_BINDING_LIMIT]: () =>
    'limitation.hints.overBindingLimit'
};

/**
 * hover 用的理由清單。三種原因共用同一個紅色，所以顏色分不出是哪一種，只能靠這裡講。
 * 順序照 `reasons`，最主要的那個排在最前面。
 */
export function statusHintKeys(
  status: DeliveryTargetStatus | undefined,
  unitKey: DeliveryTargetUnitKey
): string[] {
  return (status?.reasons ?? [])
    .map((reason) => REASON_I18N_KEYS[reason]?.(unitKey))
    .filter((key): key is string => key !== undefined);
}
